import React, { useState, useCallback } from 'react';
import { Button, FormGroup, InputGroup, TextArea, Intent, Icon } from '@blueprintjs/core';
import type { ContactFormData, ContactFormErrors } from '../../types';
import './ContactForm.scss';

const initialFormData: ContactFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: '',
};

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^[\d\s\-().+]+$/;
    return phone === '' || (phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10);
  };

  const validate = (): boolean => {
    const newErrors: ContactFormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name as keyof ContactFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));

    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData(initialFormData);
  };

  if (isSubmitted) {
    return (
      <div className="contact-form contact-form--success">
        <div className="contact-form__success">
          <Icon icon="tick-circle" size={48} intent={Intent.SUCCESS} />
          <h3>Thank You!</h3>
          <p>
            Your message has been sent successfully. I'll get back to you within 24 hours.
          </p>
          <Button
            intent="primary"
            onClick={() => setIsSubmitted(false)}
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={handleSubmit} noValidate>
      <div className="contact-form__row">
        <FormGroup
          label="First Name"
          labelFor="firstName"
          intent={errors.firstName ? Intent.DANGER : Intent.NONE}
          helperText={errors.firstName}
          className="contact-form__field"
        >
          <InputGroup
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="John"
            intent={errors.firstName ? Intent.DANGER : Intent.NONE}
            large
            leftIcon="person"
          />
        </FormGroup>

        <FormGroup
          label="Last Name"
          labelFor="lastName"
          intent={errors.lastName ? Intent.DANGER : Intent.NONE}
          helperText={errors.lastName}
          className="contact-form__field"
        >
          <InputGroup
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Doe"
            intent={errors.lastName ? Intent.DANGER : Intent.NONE}
            large
          />
        </FormGroup>
      </div>

      <div className="contact-form__row">
        <FormGroup
          label="Email"
          labelFor="email"
          intent={errors.email ? Intent.DANGER : Intent.NONE}
          helperText={errors.email}
          className="contact-form__field"
        >
          <InputGroup
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john.doe@example.com"
            intent={errors.email ? Intent.DANGER : Intent.NONE}
            large
            leftIcon="envelope"
          />
        </FormGroup>

        <FormGroup
          label="Phone (Optional)"
          labelFor="phone"
          intent={errors.phone ? Intent.DANGER : Intent.NONE}
          helperText={errors.phone}
          className="contact-form__field"
        >
          <InputGroup
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(555) 123-4567"
            intent={errors.phone ? Intent.DANGER : Intent.NONE}
            large
            leftIcon="phone"
          />
        </FormGroup>
      </div>

      <FormGroup
        label="Message"
        labelFor="message"
        intent={errors.message ? Intent.DANGER : Intent.NONE}
        helperText={errors.message}
        className="contact-form__field contact-form__field--full"
      >
        <TextArea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell me about your insurance needs or any questions you have about Living Benefits..."
          intent={errors.message ? Intent.DANGER : Intent.NONE}
          large
          fill
          growVertically
          rows={5}
        />
      </FormGroup>

      <Button
        type="submit"
        intent="primary"
        large
        fill
        loading={isSubmitting}
        rightIcon={<Icon icon="send-message" />}
        className="contact-form__submit"
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  );
};

export default ContactForm;
